import { GridSortDirection } from '@mui/x-data-grid';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { MAX_ENTITIES_NUMBER_PER_REQUEST, NUMBER_OF_RETRIES } from 'common/constants/exportData';
import { UserDetails } from 'common/types/Entities';
import { getNumberOfRequests, mergeResults } from 'common/utils/exportDataApiHelper';

import {
    APIUserDelete,
    APIUserDeleteIdentity,
    APIUserDeleteParams,
    APIUserGetSingle,
    APIUserIdentityDeleteParams,
    APIUserPatch,
    APIUserPatchParams,
    APIUsersGet,
    APIUsersGetListParams,
    APIUsersGetSingleParams,
    APIUsersGetSingleResponse,
    APIUsersMultipleIdentitiesPost,
    APIUsersMultipleIdentitiesPostParams,
    APIUsersOktaWithNewDjPost,
    APIUsersOktaWithNewDjPostParams,
    APIUsersPost,
    APIUsersPostParams,
    IUser } from './Users.api';

export const fetchUsers = createAsyncThunk('users/fetchUsers', async (params: APIUsersGetListParams) => {
    const response = await APIUsersGet(params);
    return {
        data: formatUsers(response.data),
        skipped: response.skipped,
        taken: response.taken,
        count: response.count,
    };
});

export const fetchUsersForExport = createAsyncThunk('users/fetchUsersForExport', async (params: APIUsersGetListParams) => {
    const numberOfRequests = getNumberOfRequests(params.rowsCount);
    const fetchPromises = Array.from({ length: numberOfRequests }, async (_, i) => APIUsersGet({
        ...params,
        skip: i * MAX_ENTITIES_NUMBER_PER_REQUEST,
        take: MAX_ENTITIES_NUMBER_PER_REQUEST,
        retries: NUMBER_OF_RETRIES
    }));

    const response = (await Promise.all(fetchPromises))
        .map((response)=>{
            return {
                data: formatUsers(response.data),
                skipped: response.skipped,
                taken: response.taken
            };
        });

    return mergeResults(response);
});

export const formatUsers = (rows: IUser[]) =>
    rows.map((item: IUser) =>
        ({
            ...item,
            'userId': item.id || '',
            'firstName': item.profile?.firstName || 'Unknown',
            'lastName': item.profile?.lastName || 'User',
            'principalName': item.profile?.principalName || 'Unknown',
            'userEmail': item.profile?.email || '',
            'companyKey': item.profile?.companyKey || '',
        })
    );

const _formatUserDetailsResponse = ({ data }: APIUsersGetSingleResponse): UserDetails => ({
    userId: data?.id || '',
    firstName: data?.profile?.firstName || '',
    lastName: data?.profile?.lastName || '',
    userEmail: data?.profile?.email || '',
    companyKey: data?.profile?.companyKey || '',
    oasysId: data?.oasysId ?? '',
    identities: data?.identities ?? []
});

export const postUser = createAsyncThunk('users/postUser', async (item: {post: APIUsersPostParams, take?: number, sortKey?: string, sortOrder?: GridSortDirection} ) => {
    const response = await APIUsersPost(item.post);
    const getResponse = await APIUsersGet({ take: item.take, sortKey: item.sortKey, sortOrder: item.sortOrder });
    const rows = formatUsers(getResponse.data);
    const count = getResponse.count;
    return {
        count,
        rows,
        item,
        response
    };
});

export const postUserWithMultipleIdentities = createAsyncThunk('users/postUserWithMultipleIdentities', async (item: {post: APIUsersMultipleIdentitiesPostParams, take?: number, sortKey?: string, sortOrder?: GridSortDirection} ) => {
    const response = await APIUsersMultipleIdentitiesPost(item.post);
    const getResponse = await APIUsersGet({ take: item.take, sortKey: item.sortKey, sortOrder: item.sortOrder });
    const rows = formatUsers(getResponse.data);
    const count = getResponse.count;
    return {
        count,
        rows,
        item,
        response
    };
});

export const postUserWithOktaAndNewDj = createAsyncThunk('users/postUserWithOktaAndNewDj', async (item: {post: APIUsersOktaWithNewDjPostParams, take?: number, sortKey?: string, sortOrder?: GridSortDirection} ) => {
    const response = await APIUsersOktaWithNewDjPost(item.post);
    const getResponse = await APIUsersGet({ take: item.take, sortKey: item.sortKey, sortOrder: item.sortOrder });
    const rows = formatUsers(getResponse.data);
    const count = getResponse.count;
    return {
        count,
        rows,
        item,
        response
    };
});

export const fetchUserDetails = createAsyncThunk('users/fetchUserDetails', async (params: APIUsersGetSingleParams): Promise<UserDetails> => {
    const singleUserResponse = await APIUserGetSingle(params);
    return _formatUserDetailsResponse(singleUserResponse);
});

export const patchUser = createAsyncThunk('users/patchUser', async (item: APIUserPatchParams) => {
    await APIUserPatch(item);
    const singleUserResponse = await APIUserGetSingle({ userId: item.userId });

    return _formatUserDetailsResponse(singleUserResponse);
});

export const deleteUserIdentity = createAsyncThunk('users/deleteUserIdentity', async (item: APIUserIdentityDeleteParams) => {
    await APIUserDeleteIdentity(item);
    const singleUserResponse = await APIUserGetSingle({ userId: item.userId });

    return _formatUserDetailsResponse(singleUserResponse);
});


export const deleteUser = createAsyncThunk('users/deleteUser', async (item: APIUserDeleteParams) => {
    return APIUserDelete(item);
});
